import { useState, useEffect } from "react";
import { IconX, IconCheck, IconLock } from "@tabler/icons-react";
import {
  PasswordInput,
  Progress,
  Text,
  Popover,
  Box,
  rem,
  ActionIcon,
} from "@mantine/core";

interface PasswordRequirementProps {
  meets: boolean;
  label: string;
}

export const requirements = [
  { re: /[0-9]/, label: "Includes number" },
  { re: /[a-z]/, label: "Includes lowercase letter" },
  { re: /[A-Z]/, label: "Includes uppercase letter" },
  { re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: "Includes special symbol" },
];

/** Minimum password length enforced both client-side and server-side. */
export const PASSWORD_MIN_LENGTH = 8;

/**
 * Returns true when ``candidate`` satisfies every rule in :const:`requirements`
 * plus the :const:`PASSWORD_MIN_LENGTH` floor.
 *
 * Mirrors the backend ``backend.utils.password_validation`` rules so client
 * forms can short-circuit before a network round-trip; the backend always
 * re-validates on submit, so this is strictly a UX guard.
 */
export const isLocallyValidPassword = (candidate: string): boolean => {
  if (candidate.length < PASSWORD_MIN_LENGTH) {
    return false;
  }
  return requirements.every((rule) => rule.re.test(candidate));
};

const PasswordRequirement = ({ meets, label }: PasswordRequirementProps) => {
  return (
    <Text
      c={meets ? "teal" : "red"}
      style={{ display: "flex", alignItems: "center" }}
      mt={7}
      size="sm"
      component="div"
    >
      {meets ? (
        <IconCheck style={{ width: rem(14), height: rem(14) }} />
      ) : (
        <IconX style={{ width: rem(14), height: rem(14) }} />
      )}
      <Box ml={10}>{label}</Box>
    </Text>
  );
};

function getStrength(password: string) {
  let multiplier = password.length > 7 ? 0 : 1;

  requirements.forEach((requirement) => {
    if (!requirement.re.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}

interface PasswordRequirementsProps {
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  placeholder: string;
  name: string;
  error?: string;
  variant?: "unstyled" | "default" | "filled";
  radius?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Matches auth flows (`styled-inputs` + ``variant="unstyled"``) when set to ``xs``. */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Defaults to true; set false to match login-style fields without a required marker. */
  withAsterisk?: boolean;
}

const PasswordRequirements = ({
  value,
  setValue,
  placeholder,
  name,
  error,
  variant = "unstyled",
  radius,
  size,
  withAsterisk = true,
}: PasswordRequirementsProps) => {
  const [popoverOpened, setPopoverOpened] = useState(false);

  const meetsAllRequirements =
    requirements.every((req) => req.re.test(value)) && value.length > 7;

  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(value)}
    />
  ));

  const strength = getStrength(value);
  const halfStrength = strength > 50 ? "yellow" : "red";
  const color = strength === 100 ? "teal" : halfStrength;

  useEffect(() => {
    if (meetsAllRequirements) {
      setPopoverOpened(false);
    }
  }, [meetsAllRequirements]);

  return (
    <Popover
      opened={popoverOpened}
      position="bottom"
      width="target"
      transitionProps={{ transition: "pop" }}
    >
      <Popover.Target>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={popoverOpened}
          aria-controls="password-popover"
          onFocus={() => setPopoverOpened(true)}
          onBlur={() => setPopoverOpened(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setPopoverOpened((prev) => !prev);
            }
          }}
        >
          <PasswordInput
            leftSection={<IconLock color="#A3A9B0" />}
            withAsterisk={withAsterisk}
            type="password"
            name={name}
            variant={variant}
            radius={radius}
            size={size}
            className="password"
            placeholder={placeholder}
            value={value}
            error={error}
            onChange={(event) => setValue(event.currentTarget.value)}
          />
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="xs"
            onClick={() => setPopoverOpened(false)}
          >
            <IconX size={16} />
          </ActionIcon>
        </div>
        <Progress color={color} value={strength} size={5} mb="xs" />
        <PasswordRequirement
          label="Includes at least 8 characters"
          meets={value.length > 7}
        />
        {checks}
      </Popover.Dropdown>
    </Popover>
  );
};

export default PasswordRequirements;
